import React, { useEffect, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Box,
    Typography,
    Skeleton,
    Avatar,
    Snackbar,
    Alert,
    Tabs,
    Tab
} from "@mui/material";
import { FaEye, FaEdit, FaTrash, FaCheck, FaTimes } from "react-icons/fa";
import { IoMdClose } from "react-icons/io";
import { MdOutlineCheckCircle, MdOutlineCancel, MdOutlineDelete } from "react-icons/md";
import User_Not_Found from "../../Assets/SignUp/User_Not_Found.png";
import FilterForm from "../../components/FormComponents/FilterForm";
import InputSelecter from "../../components/FormComponents/InputSelecter";
import CustomPagination from "../../components/FormComponents/CustomPagination";
import { BsFilterRight } from "react-icons/bs";
import FilterModal from "../../modal/FilterModal";
import { useDispatch, useSelector } from "react-redux";
import { ApproveAndRejectProfile, DeleteUserAdmin } from "../../Redux/Actions/UsersProfileAction";
import { toast } from "react-toastify";
import useTitle from "../../Hooks/useTitle";
import AnimatedCounter from "../../utils/AnimatedCounter";
import useScrollToTop from "../../Hooks/useScrollToTop";
import { useNavigate } from "react-router-dom";

// Import the API functions
import { getProfileAdmin, GetStatusCount } from "../../Redux/Actions/UsersProfileAction";

const Admin = () => {
    useTitle("Admin Dashboard");
    useScrollToTop();

    const [formData, setFormData] = useState({
        gender: "",
        age: "",
        height: "",
        cast: "",
        native_place: "",
        marital_status: "",
        education: "",
    });

    const [sortBy, setSortBy] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState(() => {
        const savedPage = sessionStorage.getItem('adminCurrentPage');
        return savedPage ? parseInt(savedPage, 10) : 1;
    });
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = sessionStorage.getItem('adminActiveTab');
        return savedTab || 'pending';
    });

    // State for approve/reject/delete actions
    const [actionDialog, setActionDialog] = useState({
        open: false,
        type: '', // 'approve', 'reject', or 'delete'
        userId: null,
        userName: ''
    });
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Get delete state from Redux
    const deleteState = useSelector((state) => state.deleteUserAdmin || {});

    // Local state for profiles data
    const [profilesData, setProfilesData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCounts, setTotalCounts] = useState({
        pending: 100,
        approved: 0,
        rejected: 0,
        total: 0
    });

    const [submittedFilters, setSubmittedFilters] = useState({});

    // Save active tab to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('adminActiveTab', activeTab);
    }, [activeTab]);

    useEffect(() => {
        sessionStorage.setItem('adminCurrentPage', currentPage);
    }, [currentPage]);

    // Add a separate function to fetch status counts
    const fetchStatusCounts = async () => {
        try {
            const response = await GetStatusCount()(dispatch);
            console.log('🔍 RAW STATUS COUNTS RESPONSE:', response);
            console.log('🔍 RAW STATUS COUNTS RESPONSE:', response);

            if (response && response.data) {
                const countsData = response.data.data || response.data;
                console.log('🔍 PROCESSED COUNTS DATA:', countsData);

                const counts = {
                    pending: parseInt(countsData.pending) || parseInt(countsData.pendingCount) || 0,
                    approved: parseInt(countsData.approved) || parseInt(countsData.approvedCount) || 0,
                    rejected: parseInt(countsData.rejected) || parseInt(countsData.rejectedCount) || 0,
                    total: parseInt(countsData.total) || parseInt(countsData.totalCount) || 0
                };

                console.log('🔍 PARSED COUNTS:', counts);
                setTotalCounts(counts);
                return counts;
            }
        } catch (error) {
            console.error('Error fetching status counts:', error);
            // Return current counts as fallback
            return totalCounts;
        }
    };

    // Fallback mechanism to calculate counts from profiles data
    const calculateCountsFromProfiles = (profiles) => {
        const counts = {
            pending: 0,
            approved: 0,
            rejected: 0,
            total: profiles.length
        };

        profiles.forEach(profile => {
            const status = profile?.status || 'pending';
            if (counts.hasOwnProperty(status)) {
                counts[status]++;
            }
        });

        return counts;
    };

    // Fetch profiles using the API
    useEffect(() => {
        const fetchData = async (attempt = 1) => {
            setLoading(true);
            try {
                // Build filters object
                const filters = {
                    page: currentPage,
                    ...submittedFilters,
                };

                if (sortBy) {
                    filters.sort_by = sortBy;
                }

                if (activeTab && activeTab !== 'all') {
                    filters.status = activeTab;
                }

                // Fetch profiles and status counts
                const profilesResponse = await getProfileAdmin(filters)(dispatch);

                // Fetch status counts separately with better error handling
                let statusCounts = { pending: 0, approved: 0, rejected: 0, total: 0 };

                try {
                    statusCounts = await fetchStatusCounts();
                } catch (countError) {
                    console.error('Error fetching status counts:', countError);
                }

                // Process profiles response
                if (profilesResponse.data.success) {
                    const data = profilesResponse.data.data;
                    const profiles = data.data || [];
                    setProfilesData(profiles);
                    setTotalPages(data.last_page || 1);

                    if (statusCounts.pending === 0 && statusCounts.approved === 0 && statusCounts.rejected === 0) {
                        const calculatedCounts = calculateCountsFromProfiles(profiles);
                        setTotalCounts(calculatedCounts);
                    }
                }

            } catch (error) {
                console.error(`Error fetching data (attempt ${attempt}):`, error);

                // Retry up to 2 times on network errors
                if (attempt < 3 && !error?.response) {
                    setTimeout(() => fetchData(attempt + 1), 1000 * attempt);
                    return;
                }

                const message = error?.response?.data?.message || "Failed to fetch data. Please try again.";
                toast.error(message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [dispatch, currentPage, submittedFilters, sortBy, activeTab]);

    // Debug effect to track state changes
    useEffect(() => {
        console.log('🔄 totalCounts UPDATED:', totalCounts);
        console.log('📊 Current counts - Pending:', totalCounts.pending, 'Approved:', totalCounts.approved, 'Rejected:', totalCounts.rejected);
    }, [totalCounts]);

    // Handle delete state changes
    useEffect(() => {
        if (deleteState.loading) {
            setLoading(true);
        }
    }, [deleteState.loading]);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setCurrentPage(1); // Reset to first page when changing tabs
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const resetForm = () => {
        setFormData({
            gender: "",
            age: "",
            height: "",
            cast: "",
            native_place: "",
            marital_status: "",
            education: "",
        });
        setSubmittedFilters({});
        setCurrentPage(1);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setCurrentPage(1);
        // Strip empty/null values before submitting to avoid backend validation errors
        const cleanedFilters = Object.fromEntries(
            Object.entries(formData).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
        );
        setSubmittedFilters(cleanedFilters);
    };

    const encodeUserId = (id) => {
        return btoa(id.toString())
            .replace(/\+/g, "-")
            .replace(/\//g, "_")
            .replace(/=+$/, "");
    };

    const handleViewProfile = (id) => {
        const encodedId = encodeUserId(id);
        navigate(`/user/${encodedId}`);
    };

    const getEducation = (profile) => {
        return profile?.educations && profile?.educations.length > 0
            ? profile?.educations
                .map(edu =>
                    edu.university_name
                        ? `${edu.degree} (${edu.university_name})`
                        : `${edu.degree}`
                )
                .join(", ")
            : "N/A";
    };

    const getOccupation = (profile) => {
        return profile?.occupations && profile?.occupations.length > 0
            ? profile?.occupations
                .map(occ => occ.company_name
                    ? `${occ.role} at ${occ.company_name}`
                    : `${occ.role}`
                )
                .join(", ")
            : "N/A";
    };

    const getAge = (dateOfBirth) => {
        if (!dateOfBirth) return 'N/A';
        return new Date().getFullYear() - new Date(dateOfBirth).getFullYear();
    };

    const handleEditProfile = (id) => {
        const encodedId = encodeUserId(id);
        navigate(`/admin/edit-user/${encodedId}/Edit-Profile`);
    };

    // Approve/Reject/Delete handlers
    const handleApprove = (id, name) => {
        setActionDialog({
            open: true,
            type: 'approve',
            userId: id,
            userName: name
        });
    };

    const handleReject = (id, name) => {
        setActionDialog({
            open: true,
            type: 'reject',
            userId: id,
            userName: name
        });
    };

    const handleDelete = (id, name) => {
        setActionDialog({
            open: true,
            type: 'delete',
            userId: id,
            userName: name
        });
    };

    // Function to refresh data after actions
    const refreshData = async () => {
        try {
            const filters = {
                page: currentPage,
                ...submittedFilters,
            };

            if (sortBy) {
                filters.sort_by = sortBy;
            }

            if (activeTab && activeTab !== 'all') {
                filters.status = activeTab;
            }

            // Refresh both profiles and counts
            const [response] = await Promise.all([
                getProfileAdmin(filters)(dispatch),
                fetchStatusCounts()
            ]);

            if (response.data.success) {
                const data = response.data.data;
                setProfilesData(data.data || []);
                setTotalPages(data.last_page || 1);
            }
        } catch (error) {
            console.error('Error refreshing data:', error);
            toast.error('Failed to refresh data');
        }
    };

    const confirmAction = async () => {
        const { type, userId, userName } = actionDialog;

        try {
            if (type === 'delete') {
                // Handle delete action using the Redux action
                await dispatch(DeleteUserAdmin(userId));

                setSnackbar({
                    open: true,
                    message: `Profile deleted successfully!`,
                    severity: 'success'
                });
            } else {
                // Map dialog type to status value
                const status = type === 'approve' ? 'approved' : 'rejected';

                // Dispatch the ApproveAndRejectProfile action
                await dispatch(ApproveAndRejectProfile(userId, status));

                setSnackbar({
                    open: true,
                    message: `Profile ${type === 'approve' ? 'approved' : 'rejected'} successfully!`,
                    severity: 'success'
                });
            }

            // Refresh the data to reflect the changes
            await refreshData();

        } catch (error) {
            console.error('Action error:', error);
            setSnackbar({
                open: true,
                message: `Failed to ${type} profile. ${error.response?.data?.message || 'Please try again.'}`,
                severity: 'error'
            });
        }

        setActionDialog({ open: false, type: '', userId: null, userName: '' });
    };

    const cancelAction = () => {
        setActionDialog({ open: false, type: '', userId: null, userName: '' });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Helper function to get status chip color
    const getStatusChip = (profile) => {
        const status = profile?.status || 'pending';

        const statusConfig = {
            approved: { color: '#10b981', bgColor: '#ecfdf5', label: 'Approved' },
            rejected: { color: '#ef4444', bgColor: '#fef2f2', label: 'Rejected' },
            pending: { color: '#f59e0b', bgColor: '#fffbeb', label: 'Pending' }
        };

        const config = statusConfig[status] || statusConfig.pending;

        return (
            <Chip
                label={config.label}
                size="small"
                sx={{
                    backgroundColor: config.bgColor,
                    color: config.color,
                    fontWeight: '600',
                    fontSize: '11px',
                    border: `1px solid ${config.color}20`
                }}
            />
        );
    };

    // Get modal content based on action type
    const getModalContent = () => {
        const { type, userName } = actionDialog;

        const content = {
            approve: {
                title: 'Are you sure?',
                message: `Do you really want to approve the profile of ${userName}?`,
                icon: <MdOutlineCheckCircle size={30} color="#10b981" />,
                iconBg: '#ecfdf5',
                iconBorder: '#10b981',
                confirmText: 'Approve',
                confirmColor: 'bg-[#10b981] hover:bg-[#0da271]'
            },
            reject: {
                title: 'Are you sure?',
                message: `Do you really want to reject the profile of ${userName}?`,
                icon: <MdOutlineCancel size={30} color="#ef4444" />,
                iconBg: '#fef2f2',
                iconBorder: '#ef4444',
                confirmText: 'Reject',
                confirmColor: 'bg-[#ef4444] hover:bg-[#e72848]'
            },
            delete: {
                title: 'Are you sure?',
                message: `Do you really want to delete the profile of ${userName}? This action cannot be undone and all associated data will be lost.`,
                icon: <MdOutlineDelete size={30} color="#e72848" />,
                iconBg: '#fef2f2',
                iconBorder: '#ef4444',
                confirmText: 'Delete',
                confirmColor: 'bg-[#e72848] hover:bg-[#b91c1c]'
            }
        };

        return content[type] || content.approve;
    };

    // Get the data to display
    const displayData = profilesData || [];

    return (
        <>
            <div className="md:max-w-[750px] lg:max-w-[1100px] xl:max-w-[1200px] 2xl:max-w-[1600px] mx-auto px-4 py-6">
                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Filter Sidebar */}
                    <div className="w-full lg:w-2/6 xl:w-full 2xl:w-1/5 hidden lg:block xl:hidden 2xl:block">
                        <div className="sticky top-4 animate-fade-in-side">
                            <FilterForm
                                formData={formData}
                                handleFormChange={handleFormChange}
                                handleSubmit={handleSubmit}
                                resetForm={resetForm}
                            />
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="w-full lg:w-4/6 xl:w-full 2xl:w-4/5">
                        {/* Header with Stats and Controls */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-6">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        User Profiles Management
                                    </h2>
                                    <p className="text-gray-500 text-sm mt-1">
                                        Manage and review user profiles in the system
                                    </p>
                                </div>

                                <div className="flex items-center gap-3 w-full md:w-auto">
                                    <button
                                        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg lg:hidden xl:flex 2xl:hidden hover:bg-gray-50 transition-colors text-gray-700 font-medium"
                                        onClick={() => setModalOpen(true)}
                                    >
                                        <BsFilterRight size={18} />
                                        Filter
                                    </button>
                                    <InputSelecter
                                        value={sortBy}
                                        onChange={handleSortChange}
                                        options={[
                                            // { label: "Most Relevant", value: "most_relevant" },
                                            { label: "Newest", value: "date_new" },
                                            { label: "Oldest", value: "date_old" },
                                        ]}
                                    />
                                </div>
                            </div>

                            {/* Tabs */}
                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 3 }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    <Tab
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Pending
                                                <Chip
                                                    label={totalCounts.pending || 0}
                                                    size="small"
                                                    color="warning"
                                                    variant="outlined"
                                                    sx={{
                                                        minWidth: 'auto',
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        minWidth: 24
                                                    }}
                                                />
                                            </Box>
                                        }
                                        value="pending"
                                    />
                                    <Tab
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Approved
                                                <Chip
                                                    label={totalCounts.approved || 0}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                    sx={{
                                                        minWidth: 'auto',
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        minWidth: 24
                                                    }}
                                                />
                                            </Box>
                                        }
                                        value="approved"
                                    />
                                    <Tab
                                        label={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                Rejected
                                                <Chip
                                                    label={totalCounts.rejected || 0}
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    sx={{
                                                        minWidth: 'auto',
                                                        height: 20,
                                                        fontSize: '0.7rem',
                                                        minWidth: 24
                                                    }}
                                                />
                                            </Box>
                                        }
                                        value="rejected"
                                    />
                                </Tabs>
                            </Box>
                        </div>

                        {/* MUI Table */}
                        <Paper
                            sx={{
                                width: '100%',
                                overflow: 'hidden',
                                borderRadius: '12px',
                                boxShadow: '0px 2px 12px 0px rgba(0, 0, 0, 0.06)',
                                border: '1px solid #f0f0f0'
                            }}
                        >
                            <TableContainer sx={{ maxHeight: 700 }}>
                                <Table stickyHeader aria-label="admin users table">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                            <TableCell
                                                sx={{
                                                    minWidth: 200,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Profile
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: 80,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Age
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: 100,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Status
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: 180,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Education
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: 180,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Occupation
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: 140,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Location
                                            </TableCell>
                                            <TableCell
                                                sx={{
                                                    minWidth: 130,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Religion
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                sx={{
                                                    minWidth: 180,
                                                    backgroundColor: '#f9fafb',
                                                    fontWeight: '600',
                                                    fontSize: '13px',
                                                    color: '#4b5563',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.5px',
                                                    borderBottom: '1px solid #e5e7eb',
                                                    py: 2.5
                                                }}
                                            >
                                                Actions
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            // Loading Skeletons
                                            Array.from({ length: 6 }).map((_, index) => (
                                                <TableRow key={index} sx={{ borderBottom: '1px solid #f0f0f0' }}>
                                                    <TableCell sx={{ py: 2 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <Skeleton variant="circular" width={40} height={40} />
                                                            <Skeleton variant="text" width={120} height={20} />
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={40} height={20} /></TableCell>
                                                    <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={60} height={20} /></TableCell>
                                                    <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={100} height={20} /></TableCell>
                                                    <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={100} height={20} /></TableCell>
                                                    <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={80} height={20} /></TableCell>
                                                    <TableCell sx={{ py: 2 }}><Skeleton variant="text" width={80} height={20} /></TableCell>
                                                    <TableCell sx={{ py: 2, textAlign: 'center' }}>
                                                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                            <Skeleton variant="circular" width={32} height={32} />
                                                            <Skeleton variant="circular" width={32} height={32} />
                                                            <Skeleton variant="circular" width={32} height={32} />
                                                            <Skeleton variant="circular" width={32} height={32} />
                                                            <Skeleton variant="circular" width={32} height={32} />
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : displayData.length < 1 ? (
                                            // Empty State
                                            <TableRow>
                                                <TableCell colSpan={8} align="center" sx={{ py: 8, borderBottom: 'none' }}>
                                                    <Box className="flex flex-col items-center justify-center">
                                                        <img
                                                            src={User_Not_Found}
                                                            alt="No user found"
                                                            className="max-w-xs mb-4 opacity-80"
                                                        />
                                                        <Typography variant="h6" fontWeight="600" color="#1f2937" gutterBottom>
                                                            No {activeTab} Profiles Found
                                                        </Typography>
                                                        <Typography variant="body2" color="#6b7280" className="mb-6 max-w-sm">
                                                            {activeTab === 'pending' && 'No profiles are currently pending review.'}
                                                            {activeTab === 'approved' && 'No profiles have been approved yet.'}
                                                            {activeTab === 'rejected' && 'No profiles have been rejected.'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            // Table Data
                                            displayData.map((profile, idx) => {
                                                const name = `${profile?.firstname} ${profile?.lastname}`;
                                                const age = getAge(profile?.profile?.date_of_birth);
                                                const location = `${profile?.profile?.city || 'N/A'}, ${profile?.profile?.country || 'N/A'}`;
                                                const religionCast = `${profile?.profile?.religion || 'N/A'}`;
                                                const initials = `${profile?.firstname?.charAt(0) || ''}${profile?.lastname?.charAt(0) || ''}`.toUpperCase();

                                                return (
                                                    <TableRow
                                                        key={profile?.id}
                                                        hover
                                                        sx={{
                                                            cursor: 'pointer',
                                                            borderBottom: '1px solid #f0f0f0',
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                backgroundColor: '#f9fafb',
                                                            },
                                                            py: 1.5
                                                        }}
                                                        onClick={() => handleViewProfile(profile?.id)}
                                                    >
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                <Avatar
                                                                    src={profile?.images && profile?.images.length > 0 ? profile?.images[0] : undefined}
                                                                    alt={name}
                                                                    sx={{ width: 40, height: 40, bgcolor: '#e72848', fontSize: '14px', fontWeight: '600' }}
                                                                >
                                                                    {(!profile?.images || profile?.images.length === 0) ? initials : ''}
                                                                </Avatar>
                                                                <Box>
                                                                    <Typography variant="body2" fontWeight="600" color="#1f2937">
                                                                        {name}
                                                                    </Typography>
                                                                </Box>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Chip
                                                                label={`${age} yrs`}
                                                                size="small"
                                                                sx={{
                                                                    backgroundColor: '#fce7eb',
                                                                    color: '#e72848',
                                                                    fontWeight: '600',
                                                                    fontSize: '12px'
                                                                }}
                                                            />
                                                        </TableCell>
                                                        <TableCell sx={{ py: 2 }}>
                                                            {getStatusChip(profile)}
                                                        </TableCell>
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Typography variant="body2" color="#4b5563" sx={{ fontSize: '13px', lineHeight: 1.4 }}>
                                                                {getEducation(profile).substring(0, 50)}
                                                                {getEducation(profile).length > 50 ? '...' : ''}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Typography variant="body2" color="#4b5563" sx={{ fontSize: '13px', lineHeight: 1.4 }}>
                                                                {getOccupation(profile).substring(0, 50)}
                                                                {getOccupation(profile).length > 50 ? '...' : ''}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Typography variant="body2" color="#4b5563" sx={{ fontSize: '13px' }}>
                                                                {location}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell sx={{ py: 2 }}>
                                                            <Typography variant="body2" color="#4b5563" sx={{ fontSize: '13px' }}>
                                                                {religionCast}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ py: 2 }}>
                                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleViewProfile(profile?.id);
                                                                    }}
                                                                    sx={{
                                                                        color: '#3b82f6',
                                                                        backgroundColor: '#eff6ff',
                                                                        '&:hover': { backgroundColor: '#dbeafe' },
                                                                        width: 32,
                                                                        height: 32
                                                                    }}
                                                                    title="View Profile"
                                                                >
                                                                    <FaEye size={14} />
                                                                </IconButton>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleEditProfile(profile?.id);
                                                                    }}
                                                                    sx={{
                                                                        color: '#f59e0b',
                                                                        backgroundColor: '#fffbeb',
                                                                        '&:hover': { backgroundColor: '#fef3c7' },
                                                                        width: 32,
                                                                        height: 32
                                                                    }}
                                                                    title="Edit"
                                                                >
                                                                    <FaEdit size={12} />
                                                                </IconButton>
                                                                {/* Show Approve button only for pending and rejected profiles */}
                                                                {(activeTab === 'pending' || activeTab === 'rejected') && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleApprove(profile?.id, name);
                                                                        }}
                                                                        sx={{
                                                                            color: '#10b981',
                                                                            backgroundColor: '#ecfdf5',
                                                                            '&:hover': { backgroundColor: '#d1fae5' },
                                                                            width: 32,
                                                                            height: 32
                                                                        }}
                                                                        title="Approve"
                                                                    >
                                                                        <FaCheck size={12} />
                                                                    </IconButton>
                                                                )}
                                                                {/* Show Reject button for pending and approved profiles */}
                                                                {(activeTab === 'pending' || activeTab === 'approved') && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleReject(profile?.id, name);
                                                                        }}
                                                                        sx={{
                                                                            color: '#ef4444',
                                                                            backgroundColor: '#fef2f2',
                                                                            '&:hover': { backgroundColor: '#fee2e2' },
                                                                            width: 32,
                                                                            height: 32
                                                                        }}
                                                                        title="Reject"
                                                                    >
                                                                        <FaTimes size={12} />
                                                                    </IconButton>
                                                                )}
                                                                {/* Show Delete button for all statuses */}
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleDelete(profile?.id, name);
                                                                    }}
                                                                    sx={{
                                                                        color: '#e72848',
                                                                        backgroundColor: '#fef2f2',
                                                                        '&:hover': { backgroundColor: '#fee2e2' },
                                                                        width: 32,
                                                                        height: 32
                                                                    }}
                                                                    title="Delete"
                                                                >
                                                                    <FaTrash size={12} />
                                                                </IconButton>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>

                        {/* Pagination */}
                        <div className="flex justify-center mt-8">
                            {displayData.length > 0 && (
                                <CustomPagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={setCurrentPage}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Filter Modal for Mobile */}
            <FilterModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                filterProps={{
                    formData,
                    handleFormChange,
                    handleSubmit: (e) => {
                        handleSubmit(e);
                        setModalOpen(false);
                    },
                    resetForm,
                }}
            />

            {/* Action Confirmation Modal */}
            {actionDialog.open && (
                <div
                    className="fixed inset-0 flex items-center justify-center bg-gray-800 bg-opacity-50 p-4 sm:p-0 z-50"
                    onClick={cancelAction}
                >
                    <div
                        className="bg-white p-6 rounded-3xl shadow-lg max-w-xs w-full"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-end">
                            <IoMdClose
                                size={20}
                                className="cursor-pointer text-gray-500 hover:text-gray-700"
                                onClick={cancelAction}
                            />
                        </div>
                        <div className="flex justify-center mt-3">
                            <div
                                className="rounded-full p-4 border-2"
                                style={{
                                    backgroundColor: getModalContent().iconBg,
                                    borderColor: getModalContent().iconBorder
                                }}
                            >
                                {getModalContent().icon}
                            </div>
                        </div>
                        <div className="px-4 py-8 flex flex-col items-center text-center">
                            <div className="text-xl font-bold text-gray-900">
                                {getModalContent().title}
                            </div>
                            <div className="text-gray-600 mt-2">
                                {getModalContent().message}
                            </div>
                            {actionDialog.type === 'delete' && (
                                <div className="text-red-600 font-semibold text-sm mt-3">
                                    ⚠️ This action cannot be undone!
                                </div>
                            )}
                        </div>
                        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-5">
                            <button
                                className="px-6 py-2 border border-gray-300 rounded-xl w-full sm:w-auto hover:bg-gray-50 transition-colors font-medium"
                                onClick={cancelAction}
                            >
                                Cancel
                            </button>
                            <button
                                className={`px-6 py-2 text-white rounded-xl w-full sm:w-auto transition-colors font-medium ${getModalContent().confirmColor}`}
                                onClick={confirmAction}
                                disabled={loading}
                            >
                                {loading ? 'Processing...' : getModalContent().confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Admin;